import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Feather, Loader2, LogOut, MailOpen, RefreshCw, School } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-teal-100 text-teal-800 border-teal-200",
  cancelled: "bg-stone-200 text-stone-600 border-stone-300",
  new: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-teal-100 text-teal-800 border-teal-200",
  published: "bg-teal-100 text-teal-800 border-teal-200",
  draft: "bg-stone-200 text-stone-600 border-stone-300",
};

const inputCls =
  "w-full rounded-xl border border-pine-900/15 bg-cream px-4 py-3 text-sm text-pine-950 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-stone-400 focus:border-pine-700 focus:ring-2 focus:ring-pine-700/15";

const BrandLockup = () => (
  <>
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-1 ring-1 ring-pine-900/10">
      <img src="/assets/bee.jpg" alt="SpellingBee logo" className="h-full w-full rounded-full object-contain" />
    </span>
    <span className="leading-none">
      <span className="block font-serif text-lg font-semibold tracking-tight text-pine-950">SpellingBee</span>
      <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.22em] text-pine-700/70">Therapy Services</span>
    </span>
  </>
);

const bodyToText = (body = []) =>
  (body || [])
    .map((s) => [s.heading ? `## ${s.heading}` : "", ...(s.paragraphs || [])].filter(Boolean).join("\n"))
    .join("\n\n");

const textToBody = (text) => {
  const sections = [];
  let current = { heading: null, paragraphs: [] };
  let para = [];
  const flushPara = () => {
    if (para.length) {
      current.paragraphs.push(para.join(" "));
      para = [];
    }
  };
  const flushSection = () => {
    flushPara();
    if (current.heading || current.paragraphs.length) sections.push(current);
    current = { heading: null, paragraphs: [] };
  };
  text.split("\n").forEach((line) => {
    const t = line.trimEnd();
    if (t.startsWith("## ")) {
      flushSection();
      current.heading = t.slice(3).trim();
    } else if (t.trim() === "") {
      flushPara();
    } else {
      para.push(t.trim());
    }
  });
  flushSection();
  return sections;
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const EMPTY_ARTICLE = { title: "", slug: "", tag: "", read: "5 min read", excerpt: "", bodyText: "", published: false };

const ArticleEditor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const save = async (publish) => {
    if (!f.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      title: f.title,
      slug: f.slug.trim() || slugify(f.title),
      tag: f.tag || "General",
      read: f.read || "5 min read",
      excerpt: f.excerpt,
      body: textToBody(f.bodyText),
      published: publish ?? f.published,
    };
    try {
      if (f.id) await axios.put(`${API}/admin/articles/${f.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/articles`, payload, { withCredentials: true });
      toast.success(payload.published ? "Article published" : "Draft saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-3xl border border-pine-900/10 bg-white p-8 sm:p-10" data-testid="article-editor">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input data-testid="article-form-title" className={inputCls} placeholder="Article title" value={f.title} onChange={set("title")} />
        <input data-testid="article-form-slug" className={inputCls} placeholder="Web address (slug) — auto if empty" value={f.slug} onChange={set("slug")} />
        <input data-testid="article-form-tag" className={inputCls} placeholder="Topic tag (e.g. For Parents)" value={f.tag} onChange={set("tag")} />
        <input data-testid="article-form-read" className={inputCls} placeholder="Read time (e.g. 5 min read)" value={f.read} onChange={set("read")} />
      </div>
      <textarea
        data-testid="article-form-excerpt"
        className={`${inputCls} mt-4 min-h-[70px] resize-none`}
        placeholder="Excerpt — one or two sentences shown on the journal card"
        value={f.excerpt}
        onChange={set("excerpt")}
      />
      <textarea
        data-testid="article-form-body"
        className={`${inputCls} mt-4 min-h-[320px] resize-y font-mono text-[13px] leading-relaxed`}
        placeholder={"Write the article here.\n\nStart a section heading with: ## Heading text\nSeparate paragraphs with a blank line."}
        value={f.bodyText}
        onChange={set("bodyText")}
      />
      <p className="mt-2 text-xs text-stone-400">
        Tip: lines starting with <span className="font-mono">##</span> become section headings; blank lines separate paragraphs.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          data-testid="article-form-save"
          onClick={() => save()}
          disabled={saving}
          className="flex items-center gap-2 rounded-full border border-pine-900/20 px-6 py-3 text-sm font-semibold text-pine-900 transition-colors hover:bg-sand disabled:opacity-40"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {f.published ? "Save changes" : "Save as draft"}
        </button>
        <button
          data-testid="article-form-publish"
          onClick={() => save(true)}
          disabled={saving}
          className="flex items-center gap-2 rounded-full bg-pine-900 px-6 py-3 text-sm font-semibold text-cream transition-colors hover:bg-pine-800 disabled:opacity-40"
        >
          {f.published ? "Save & keep published" : "Publish article"}
        </button>
        <button
          data-testid="article-form-cancel"
          onClick={onCancel}
          className="rounded-full px-6 py-3 text-sm font-semibold text-stone-500 transition-colors hover:text-pine-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [schoolEnquiries, setSchoolEnquiries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, e, s, a] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/enquiries`, { withCredentials: true }),
        axios.get(`${API}/school-enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/articles`, { withCredentials: true }),
      ]);
      setBookings(b.data);
      setEnquiries(e.data);
      setSchoolEnquiries(s.data);
      setArticles(a.data);
    } catch {
      toast.error("Couldn't load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const updateStatus = async (kind, id, status) => {
    try {
      const { data } = await axios.patch(`${API}/${kind}/${id}`, { status }, { withCredentials: true });
      if (kind === "bookings") setBookings((rows) => rows.map((r) => (r.id === id ? data : r)));
      else if (kind === "school-enquiries") setSchoolEnquiries((rows) => rows.map((r) => (r.id === id ? data : r)));
      else setEnquiries((rows) => rows.map((r) => (r.id === id ? data : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const togglePublish = async (a) => {
    try {
      await axios.put(
        `${API}/admin/articles/${a.id}`,
        { title: a.title, slug: a.slug, tag: a.tag, read: a.read, excerpt: a.excerpt, body: a.body || [], published: !a.published },
        { withCredentials: true }
      );
      toast.success(a.published ? "Unpublished — now a draft" : "Published — live on the site");
      load();
    } catch {
      toast.error("Couldn't update article");
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, { withCredentials: true });
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Couldn't delete article");
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="animate-spin text-pine-700" size={28} data-testid="admin-loading" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-pine-900/10 bg-white p-9 shadow-xl shadow-pine-900/5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-login-brand">
            <BrandLockup />
          </Link>
          <h1 className="mt-8 font-serif text-2xl font-semibold text-pine-950">Studio admin</h1>
          <p className="mt-1 text-sm text-stone-500">Sign in to view bookings and enquiries.</p>
          <div className="mt-7 space-y-4">
            <input
              data-testid="admin-login-username"
              className={inputCls}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              data-testid="admin-login-password"
              className={inputCls}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loggingIn}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-pine-900 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-pine-800 disabled:opacity-50"
            >
              {loggingIn && <Loader2 size={15} className="animate-spin" />}
              Sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  const rows = tab === "bookings" ? bookings : tab === "schools" ? schoolEnquiries : enquiries;
  const pendingCount =
    bookings.filter((b) => b.status === "pending").length +
    enquiries.filter((e) => e.status === "new").length +
    schoolEnquiries.filter((s) => s.status === "new").length;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-pine-900/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-brand">
            <BrandLockup />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-stone-500 sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-pine-900/15 px-4 py-2 text-xs font-semibold text-pine-900 transition-colors hover:bg-sand"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-pine-950 sm:text-3xl">Submissions</h1>
            <p className="mt-1 text-sm text-stone-500" data-testid="admin-pending-count">
              {pendingCount} item{pendingCount === 1 ? "" : "s"} awaiting action
            </p>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="flex items-center gap-2 rounded-full border border-pine-900/15 px-5 py-2.5 text-xs font-semibold text-pine-900 transition-colors hover:bg-sand"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {[
            ["bookings", `Bookings (${bookings.length})`, CalendarDays],
            ["enquiries", `Enquiries (${enquiries.length})`, MailOpen],
            ["schools", `Schools (${schoolEnquiries.length})`, School],
            ["articles", `Articles (${articles.length})`, Feather],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => { setTab(key); setEditing(null); }}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === key ? "bg-pine-900 text-cream" : "border border-pine-900/15 text-pine-900 hover:bg-sand"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "articles" ? (
          editing ? (
            <ArticleEditor
              initial={editing}
              onSaved={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div className="mt-6 overflow-x-auto rounded-3xl border border-pine-900/10 bg-white" data-testid="admin-articles-table">
              <div className="flex items-center justify-between border-b border-pine-900/10 px-6 py-4">
                <p className="text-sm text-stone-500">
                  Drafts are invisible to visitors. Publish when you're happy with the text.
                </p>
                <button
                  data-testid="admin-articles-new-button"
                  onClick={() => setEditing({ ...EMPTY_ARTICLE })}
                  className="rounded-full bg-pine-900 px-5 py-2.5 text-xs font-semibold text-cream transition-colors hover:bg-pine-800"
                >
                  + New article
                </button>
              </div>
              {articles.length === 0 ? (
                <p className="px-8 py-16 text-center text-sm text-stone-500" data-testid="admin-articles-empty">
                  {loading ? "Loading…" : "No articles yet."}
                </p>
              ) : (
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-pine-900/10 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">
                      <th className="px-6 py-4">Title</th>
                      <th className="px-6 py-4">Tag</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((a) => (
                      <tr key={a.id} className="border-b border-pine-900/5 last:border-0 hover:bg-sand/50" data-testid={`admin-article-row-${a.slug}`}>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-pine-950">{a.title}</p>
                          <p className="font-mono text-xs text-stone-400">/journal/{a.slug}</p>
                        </td>
                        <td className="px-6 py-4 text-stone-600">{a.tag}</td>
                        <td className="px-6 py-4">
                          <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLE[a.published ? "published" : "draft"]}`} data-testid={`admin-article-status-${a.slug}`}>
                            {a.published ? "published" : "draft"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              data-testid={`admin-article-edit-${a.slug}`}
                              onClick={() => setEditing({ ...a, bodyText: bodyToText(a.body) })}
                              className="rounded-full border border-pine-900/15 px-4 py-1.5 text-xs font-semibold text-pine-900 transition-colors hover:bg-sand"
                            >
                              Edit
                            </button>
                            <button
                              data-testid={`admin-article-publish-${a.slug}`}
                              onClick={() => togglePublish(a)}
                              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                                a.published
                                  ? "border border-stone-300 text-stone-600 hover:bg-stone-100"
                                  : "bg-honey text-pine-950 hover:bg-[#ffd95e]"
                              }`}
                            >
                              {a.published ? "Unpublish" : "Publish"}
                            </button>
                            <button
                              data-testid={`admin-article-delete-${a.slug}`}
                              onClick={() => deleteArticle(a)}
                              className="rounded-full border border-red-200 px-4 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )
        ) : (
        <div className="mt-6 overflow-x-auto rounded-3xl border border-pine-900/10 bg-white" data-testid="admin-submissions-table">
          {rows.length === 0 ? (
            <p className="px-8 py-16 text-center text-sm text-stone-500" data-testid="admin-empty-state">
              {loading ? "Loading…" : `No ${tab} yet. New submissions from the site appear here instantly.`}
            </p>
          ) : tab === "bookings" ? (
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-pine-900/10 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">
                  <th className="px-6 py-4">Client</th>
                  <th className="px-6 py-4">Service</th>
                  <th className="px-6 py-4">Slot</th>
                  <th className="px-6 py-4">Role</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-pine-900/5 last:border-0 hover:bg-sand/50" data-testid={`admin-booking-row-${b.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-pine-950">{b.name}</p>
                      <p className="text-xs text-stone-500">{b.email} · {b.phone}</p>
                    </td>
                    <td className="px-6 py-4 text-stone-600">{b.service}</td>
                    <td className="px-6 py-4 font-mono text-xs text-stone-600">{b.date} · {b.time}</td>
                    <td className="px-6 py-4 text-stone-600">{b.role || "—"}</td>
                    <td className="px-6 py-4">
                      <select
                        data-testid={`admin-booking-status-${b.id}`}
                        value={b.status}
                        onChange={(e) => updateStatus("bookings", b.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize outline-none ${STATUS_STYLE[b.status]}`}
                      >
                        <option value="pending">pending</option>
                        <option value="confirmed">confirmed</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "schools" ? (
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-pine-900/10 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">
                  <th className="px-6 py-4">School</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4">Students</th>
                  <th className="px-6 py-4">Programme</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {schoolEnquiries.map((s) => (
                  <tr key={s.id} className="border-b border-pine-900/5 last:border-0 hover:bg-sand/50" data-testid={`admin-school-row-${s.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-pine-950">{s.school_name}</p>
                      {s.message && <p className="max-w-[220px] truncate text-xs text-stone-500">{s.message}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-pine-950">{s.name}</p>
                      <p className="text-xs text-stone-500">{s.email} · {s.phone}</p>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-stone-600">{s.student_count}</td>
                    <td className="px-6 py-4 text-stone-600">{s.programme}</td>
                    <td className="px-6 py-4">
                      <select
                        data-testid={`admin-school-status-${s.id}`}
                        value={s.status}
                        onChange={(e) => updateStatus("school-enquiries", s.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize outline-none ${STATUS_STYLE[s.status]}`}
                      >
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead>
                <tr className="border-b border-pine-900/10 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">
                  <th className="px-6 py-4">From</th>
                  <th className="px-6 py-4">Message</th>
                  <th className="px-6 py-4">Received</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((en) => (
                  <tr key={en.id} className="border-b border-pine-900/5 last:border-0 hover:bg-sand/50" data-testid={`admin-enquiry-row-${en.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-pine-950">{en.name}</p>
                      <p className="text-xs text-stone-500">{en.email}</p>
                    </td>
                    <td className="max-w-xs px-6 py-4 text-stone-600">
                      <p className="line-clamp-2">{en.message}</p>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-stone-500">
                      {new Date(en.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        data-testid={`admin-enquiry-status-${en.id}`}
                        value={en.status}
                        onChange={(e) => updateStatus("enquiries", en.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize outline-none ${STATUS_STYLE[en.status]}`}
                      >
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </main>
    </div>
  );
}
